import React from 'react'
// libs
import { Outlet } from 'react-router'
import { Cookies } from 'react-cookie'
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { getAccessToken, setAccessToken } from 'lib/auth'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// components
import PageLoader from 'components/UI/Loader/PageLoader'
import AuthWarning from './components/AuthWarning'
import SessionExpiringModal from 'components/Modals/SessionExpiringModal'
// constants
import { LICENCES_NAMES } from 'constants/licences'
import { REDIRECT_URL_FIELD } from 'constants/cookies'
// containers
import SessionProvider from 'providers/SessionProvider'
import InitialDataContainer from 'containers/InitialDataContainer'
// graphql
import { AUTH_REFRESH_TOKEN_MUTATION } from 'graphql/auth/mutations'
// types
import type { RefreshTokenResponse } from 'providers/SessionProvider/types'
// utils
import { get, isEmpty } from 'lodash'
import { getAvailablePages } from './utils'

enum AuthState {
  CHECKING = 'CHECKING',
  AUTHENTICATED = 'AUTHENTICATED',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
}

const browserCookies = new Cookies()

/**
 * Layout route that guards all protected pages.
 * On page reload (access token lost from memory) — attempts to restore the session
 * via authRefreshToken (HTTP-only refresh cookie is sent automatically).
 */
const ProtectedRoute: React.FC = () => {
  const router = useRouter()
  const user = useCurrentUser()

  const [authState, setAuthState] = React.useState<AuthState>(() =>
    getAccessToken() ? AuthState.AUTHENTICATED : AuthState.CHECKING
  )

  const [refreshTokenMt] = useMutation<RefreshTokenResponse>(AUTH_REFRESH_TOKEN_MUTATION)

  // On page reload: if no access token in memory, try to restore session via refresh token cookie
  React.useEffect(() => {
    if (authState !== AuthState.CHECKING) {
      return
    }

    let cancelled = false

    const restoreSession = async () => {
      try {
        const { data } = await refreshTokenMt()

        if (!cancelled && data?.authRefreshToken?.ok) {
          setAccessToken(data.authRefreshToken.data.accessToken)
          setAuthState(AuthState.AUTHENTICATED)
          return
        }
      } catch {
        // Refresh failed — session expired or revoked
      }

      if (!cancelled) {
        setAuthState(AuthState.UNAUTHENTICATED)
      }
    }

    restoreSession()

    return () => {
      cancelled = true
    }
  }, [])

  // Redirect to login when unauthenticated
  React.useEffect(() => {
    if (authState === AuthState.UNAUTHENTICATED) {
      browserCookies.set(REDIRECT_URL_FIELD, router.asPath, { path: '/' })
      router.replace('/login')
    }
  }, [authState])

  const permissions = user?.permissionsFromSecurityGroups || {}
  const canViewRisks: boolean = get(
    user?.company?.licences?.find(({ name }) => name === LICENCES_NAMES.FORMS),
    'active',
    false
  )

  const availablePages = getAvailablePages({ ...permissions, risksRegister: canViewRisks })

  // Permission guard: go back if user doesn't have access to this page
  React.useLayoutEffect(() => {
    if (authState !== AuthState.AUTHENTICATED || !user?.isAuthorized) {
      return
    }

    const { pathname } = router

    const noAccess =
      (pathname === '/home' && !permissions.home) ||
      (pathname.includes('forms') && !canViewRisks) ||
      (pathname.startsWith('/plans') && !permissions.planRegister) ||
      (pathname.startsWith('/kpis') && !permissions.kpiRegister) ||
      (pathname.startsWith('/actions') && !permissions.actionRegister) ||
      (pathname.startsWith('/objectives') && !permissions.objectiveRegister) ||
      (pathname.includes('data-hub') && !permissions.reporting) ||
      (pathname === '/settings/ai-usage' && !permissions.aiViewUsage) ||
      (pathname.includes('settings') && pathname !== '/settings/ai-usage' && !permissions.settings)

    if (noAccess) {
      if (pathname === '/home') {
        const firstAvailable = availablePages[0]

        if (firstAvailable) {
          router.replace(firstAvailable)
        }
      } else {
        router.back()
      }
    }
  }, [router.pathname, user?.isAuthorized, authState])

  if (authState === AuthState.CHECKING) {
    return <PageLoader isLoading />
  }

  if (authState === AuthState.UNAUTHENTICATED) {
    return null
  }

  // Token was cleared externally (manual logout) while authState is still AUTHENTICATED.
  // Avoid rendering children — navigation to /login is already in flight.
  if (!getAccessToken()) {
    return null
  }

  // No available pages at all — show blocked message
  if (isEmpty(availablePages) && user?.isAuthorized) {
    return <AuthWarning />
  }

  return (
    <SessionProvider>
      <InitialDataContainer>
        <Outlet />
        <SessionExpiringModal />
      </InitialDataContainer>
    </SessionProvider>
  )
}

export default ProtectedRoute
